import { z } from 'zod';
import type { BilibiliReferenceMetadata, BilibiliReferenceMetadataResponse } from '@reados/contracts';

export const bilibiliMetadataRequestSchema = z.object({
  bvid: z.string().regex(/^BV[A-Za-z0-9]{10}$/),
  pageNumber: z.number().int().min(1).max(10_000),
}).strict();
const label = z.string().trim().min(1).max(500).refine(value => !/[\u0000-\u001f\u007f]/.test(value));
const responseSchema = z.object({ metadata: z.object({
  ...bilibiliMetadataRequestSchema.shape, title: label, partTitle: label.optional(),
}).strict().nullable() }).strict();

/** A transcript-scoped cache. Only the fixed same-origin endpoint can supply titles. */
export class BilibiliMetadataCache {
  private cache = new Map<string, BilibiliReferenceMetadata>();
  private pending = new Map<string, Promise<BilibiliReferenceMetadata | null>>();
  private queue: Array<{ request: z.infer<typeof bilibiliMetadataRequestSchema>; key: string;
    finish: (value: BilibiliReferenceMetadata | null) => void }> = [];
  private controllers = new Set<AbortController>();
  private disposed = false;

  constructor(private origin: string, private fetcher: typeof fetch = (input, init) => fetch(input, init)) {}

  get(value: unknown): Promise<BilibiliReferenceMetadata | null> {
    const parsed = bilibiliMetadataRequestSchema.safeParse(value);
    if (this.disposed || !parsed.success) return Promise.resolve(null);
    const request = parsed.data, key = `${request.bvid}:${request.pageNumber}`;
    const cached = this.cache.get(key);
    if (cached) { this.cache.delete(key); this.cache.set(key, cached); return Promise.resolve(cached); }
    const existing = this.pending.get(key);
    if (existing) return existing;
    if (this.pending.size >= 200) return Promise.resolve(null);
    const pending = new Promise<BilibiliReferenceMetadata | null>(finish => { this.queue.push({ request, key, finish }); });
    this.pending.set(key, pending); this.drain();
    return pending;
  }

  private drain() {
    while (!this.disposed && this.controllers.size < 4 && this.queue.length) {
      const job = this.queue.shift()!;
      void this.load(job.request).then(metadata => {
        this.pending.delete(job.key);
        if (metadata && !this.disposed) {
          this.cache.set(job.key, metadata);
          while (this.cache.size > 200) this.cache.delete(this.cache.keys().next().value!);
        }
        job.finish(metadata); this.drain();
      });
    }
  }

  private async load(request: z.infer<typeof bilibiliMetadataRequestSchema>): Promise<BilibiliReferenceMetadata | null> {
    const controller = new AbortController(); this.controllers.add(controller);
    const timeout = setTimeout(() => controller.abort(), 6_000);
    try {
      const url = new URL('/api/video-references/bilibili', this.origin);
      url.searchParams.set('bvid', request.bvid); url.searchParams.set('page', String(request.pageNumber));
      const response = await this.fetcher(url.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok || this.disposed) return null;
      const result = responseSchema.safeParse(await response.json());
      if (!result.success || this.disposed) return null;
      const metadata: BilibiliReferenceMetadataResponse['metadata'] = result.data.metadata;
      return metadata?.bvid === request.bvid && metadata.pageNumber === request.pageNumber ? metadata : null;
    } catch { return null; }
    finally { clearTimeout(timeout); this.controllers.delete(controller); }
  }

  dispose() {
    this.disposed = true; this.controllers.forEach(controller => controller.abort());
    this.controllers.clear(); this.cache.clear(); this.pending.clear();
    this.queue.splice(0).forEach(job => job.finish(null));
  }
}
